import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import Typography from "@material-ui/core/Typography";
import WarningIcon from "@material-ui/icons/Warning";
import groupBy from "lodash/groupBy";
import uniq from "lodash/uniq";
import React from "react";
import { useTranslate } from "react-admin";

import { OutDocWarningCode, SourceDocErrorKind } from "../../../../entities";
import { SimpleDialog } from "../../../dialogs/Dialog";

const SourceDocErrorText = ({ error }) => {
	const translate = useTranslate();

	if (error.kind === SourceDocErrorKind.requiredCellEmpty) {
		const field = translate(`resources.mappers.fields.data_field.${error.field}`, {
			_: error.field,
		});
		return translate("osfc.dialogs.out_doc_warnings.error.required_cell_empty", {
			row: error.row,
			field,
		});
	}
	return translate(`osfc.dialogs.out_doc_warnings.error.${error.kind}`, { _: error.kind });
};

const OutDocWarningItem = ({ warning }) => {
	const translate = useTranslate();

	if (warning.code !== OutDocWarningCode.sourceDocInvalid) {
		return (
			<ListItem>
				<ListItemIcon>
					<WarningIcon color="error" />
				</ListItemIcon>
				<ListItemText
					primary={translate(`osfc.dialogs.out_doc_warnings.${warning.code}`, { _: warning.code })}
				/>
			</ListItem>
		);
	}
	return (
		<ListItem alignItems="flex-start">
			<ListItemIcon>
				<WarningIcon color="error" />
			</ListItemIcon>
			<ListItemText
				primary={translate("osfc.dialogs.out_doc_warnings.source_doc_invalid", {
					document: warning.document_title,
				})}
				primaryTypographyProps={{ variant: "subtitle1", color: "error" }}
				secondaryTypographyProps={{ component: "div", variant: "body1", color: "textPrimary" }}
				secondary={
					<>
						{warning.errors.map((error) => (
							<div key={`${error.kind}-${error.row}-${error.field}`}>
								<SourceDocErrorText error={error} />
							</div>
						))}
						{warning.truncated_count > 0 && (
							<div>
								{translate("osfc.dialogs.out_doc_warnings.truncated", {
									count: warning.truncated_count,
								})}
							</div>
						)}
					</>
				}
			/>
		</ListItem>
	);
};

export const OutDocWarningsDialog = ({ warnings, onClose }) => {
	const translate = useTranslate();
	const warningsByOrderId = groupBy(warnings, "order_id");
	const orderIds = uniq(warnings.map((warning) => warning.order_id));

	return (
		<SimpleDialog
			size="md"
			open={warnings.length > 0}
			onClose={onClose}
			title={translate("osfc.dialogs.out_doc_warnings.title")}
		>
			{orderIds.map((orderId) => (
				<React.Fragment key={orderId}>
					<Typography variant="h6">
						{translate("osfc.dialogs.out_doc_warnings.order", { order_id: orderId })}
					</Typography>
					<List>
						{warningsByOrderId[orderId].map((warning) => (
							<OutDocWarningItem key={`${warning.code}-${warning.document_id}`} warning={warning} />
						))}
					</List>
				</React.Fragment>
			))}
		</SimpleDialog>
	);
};
